import { Dialog, Transition } from "@headlessui/react";
import { ArrowPathIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { isArray, isFunction } from "lodash";
import { Fragment, useEffect, useState } from "react";
import { getUserActivityLog, getUserActivityLogCount } from "../../services/ api_services";
import useLoading from "../../hooks/useLoading";
import useUnlockUser from "../../hooks/useUnlockUser";
import Pagination from "../Pagination";
import { ACCOUNT_LOCK_STATUS, formatDate, getAccountLockLevel } from "../../core/Util";
import AccountLogEmptyState from "./AccountLogEmptyState";
import { LockIcon, LockOpenIcon } from "../icons/LockIcons";

const PROCESS_BADGE = {
    LOGIN_LOCK_TIER2: {
        label: "영구 잠금",
        icon: LockIcon,
        className: "inline-flex items-center gap-x-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-600/10",
    },
    LOGIN_AUTO_UNLOCK: {
        label: "자동 해제",
        icon: LockOpenIcon,
        className:
            "inline-flex items-center gap-x-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-600/20",
    },
    LOGIN_ADMIN_UNLOCK: {
        label: "관리자 해제",
        icon: LockOpenIcon,
        className:
            "inline-flex items-center gap-x-1 rounded-full bg-green-50 px-2 py-0.5 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-600/20",
    },
    LOGIN_LOCK_TIER1: { label: "임시 잠금" },
};

function ProcessBadge({ processNm }) {
    const badge = PROCESS_BADGE[processNm];
    if (!badge) return <span className="text-sm text-gray-500">{processNm}</span>;
    if (!badge.className) return <span className="text-sm text-gray-500">{badge.label}</span>;
    const Icon = badge.icon;
    return (
        <span className={badge.className}>
            {Icon && <Icon className="h-3 w-3" />}
            {badge.label}
        </span>
    );
}

export default function AccountSecurityDialog({
    isOpen,
    onClose,
    userSeq,
    loginLockedUntil,
    loginPermanentlyLocked,
    onUnlockSuccess,
}) {
    const { loading, setLoading } = useLoading();
    const requestUnlock = useUnlockUser();

    const [lockLevel, setLockLevel] = useState(() =>
        getAccountLockLevel({ login_permanently_locked: loginPermanentlyLocked, login_locked_until: loginLockedUntil })
    );
    const [list, setList] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const pageSize = 20;

    useEffect(() => {
        setLockLevel(getAccountLockLevel({ login_permanently_locked: loginPermanentlyLocked, login_locked_until: loginLockedUntil }));
    }, [loginPermanentlyLocked, loginLockedUntil]);

    useEffect(() => {
        if (isOpen && userSeq) {
            fetchActivityLog(currentPage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, currentPage, userSeq]);

    const fetchActivityLog = async (page) => {
        setLoading(true);
        setFetchError(false);
        const requestObj = { user_seq: userSeq, page_size: pageSize, page_number: page };
        try {
            const [listResponse, countResponse] = await Promise.all([
                getUserActivityLog(requestObj),
                getUserActivityLogCount(requestObj),
            ]);
            setLoading(false);
            if (listResponse && listResponse.status === 200 && isArray(listResponse.data)) {
                setList(listResponse.data);
            } else {
                setList([]);
                setFetchError(true);
            }
            if (countResponse && countResponse.status === 200) {
                setTotalCount(Number(countResponse.data));
            } else {
                setTotalCount(0);
            }
            setHasFetchedOnce(true);
        } catch (e) {
            setLoading(false);
            setFetchError(true);
            setHasFetchedOnce(true);
            console.log("e===>", e);
        }
    };

    const handleOnClickUnlock = async () => {
        const success = await requestUnlock(userSeq);
        if (success) {
            setLockLevel("normal");
            fetchActivityLog(currentPage);
            if (onUnlockSuccess && isFunction(onUnlockSuccess)) {
                onUnlockSuccess();
            }
        }
    };

    const handleOnClose = () => {
        setCurrentPage(1);
        setHasFetchedOnce(false);
        setFetchError(false);
        onClose();
    };

    const statusStyle = ACCOUNT_LOCK_STATUS[lockLevel];

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={() => handleOnClose()}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>
                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                                {/* 헤더: 제목 · 상태 · 액션을 한 줄에 정렬 (닫기 버튼도 흐름 안에 둬서 보정 여백이 필요 없다) */}
                                <div className="flex items-center justify-between gap-x-3 border-b border-slate-200 px-5 py-3">
                                    <Dialog.Title as="h3" className="min-w-0 truncate text-sm font-semibold text-gray-900">
                                        계정 상태 및 활동 로그
                                    </Dialog.Title>

                                    <div className="flex shrink-0 items-center gap-x-2">
                                        <span
                                            className="inline-flex h-[26px] items-center gap-x-1.5 rounded-full px-2.5 text-xs font-medium"
                                            style={{ background: statusStyle.bg, border: `1px solid ${statusStyle.border}`, color: statusStyle.ink }}
                                        >
                                            <span className="relative flex h-1.5 w-1.5">
                                                <span
                                                    className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60"
                                                    style={{ background: statusStyle.dot }}
                                                />
                                                <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ background: statusStyle.dot }} />
                                            </span>
                                            {statusStyle.label}
                                        </span>

                                        {lockLevel !== "normal" && (
                                            <button
                                                type="button"
                                                disabled={loading}
                                                className="motion-press inline-flex h-[26px] items-center rounded-md bg-blue-600 px-2.5 text-xs font-semibold text-white shadow-sm hover:bg-blue-500"
                                                onClick={() => handleOnClickUnlock()}
                                            >
                                                잠금 해제
                                            </button>
                                        )}

                                        <span className="h-4 w-px bg-slate-200" aria-hidden="true" />

                                        <button
                                            type="button"
                                            disabled={loading}
                                            title="활동 로그 새로고침"
                                            className="motion-press group inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                            onClick={() => fetchActivityLog(currentPage)}
                                        >
                                            <span className="sr-only">새로고침</span>
                                            <ArrowPathIcon
                                                className={
                                                    loading
                                                        ? "h-4 w-4 animate-spin"
                                                        : "h-4 w-4 transition-transform duration-300 ease-out group-hover:-rotate-180"
                                                }
                                                aria-hidden="true"
                                            />
                                        </button>

                                        <button
                                            type="button"
                                            title="닫기"
                                            className="motion-press inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                            onClick={() => handleOnClose()}
                                        >
                                            <span className="sr-only">닫기</span>
                                            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                    </div>
                                </div>

                                <div className="px-5 pb-5">

                                {fetchError ? (
                                    <div className="mt-4 flex h-80 items-center justify-center rounded-xl ring-1 ring-slate-200 text-sm text-slate-500">
                                        활동 로그를 불러오지 못했습니다.
                                    </div>
                                ) : hasFetchedOnce && !loading && list.length === 0 ? (
                                    <div className="mt-4">
                                        <AccountLogEmptyState />
                                    </div>
                                ) : (
                                    <>
                                        <div className="thin-scrollbar mt-4 max-h-96 overflow-y-auto rounded-xl ring-1 ring-slate-200">
                                            <table className="min-w-full divide-y divide-slate-200">
                                                <thead className="sticky top-0 z-10 bg-gradient-to-b from-slate-50 to-slate-200">
                                                    <tr>
                                                        <th scope="col" className="border-b border-slate-200 p-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">№</th>
                                                        <th scope="col" className="border-b border-slate-200 p-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">일시</th>
                                                        <th scope="col" className="border-b border-slate-200 p-3 text-center text-xs font-semibold uppercase tracking-wide text-slate-600">구분</th>
                                                        <th scope="col" className="border-b border-gray-200 p-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">내용</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                    {list.map((item, index) => (
                                                        <tr key={item.id ?? index}>
                                                            <td className="whitespace-nowrap px-3 py-2 text-center text-sm text-gray-400">
                                                                {(currentPage - 1) * pageSize + index + 1}
                                                            </td>
                                                            <td className="whitespace-nowrap px-3 py-2 text-center text-sm text-gray-500">{formatDate(item.reg_dt)}</td>
                                                            <td className="whitespace-nowrap px-3 py-2 text-center"><ProcessBadge processNm={item.process_nm} /></td>
                                                            <td className="px-3 py-2 text-left text-sm text-gray-500">{item.detail}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <Pagination
                                            onChangeCurrentPage={(val) => setCurrentPage(val)}
                                            totalCount={totalCount}
                                            itemsPerPage={pageSize}
                                        />
                                    </>
                                )}
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
